import type {
	Milestone,
	MiniStat,
	OverallProgress,
} from "@memorang/ui/components/LessonDetailStatsTab/types";
import type { ModuleArtifact } from "@memorang/ui/components/LessonDetailViewScreen/types";
import type { TopicFilter } from "@memorang/ui/components/LessonsExploreScreen/types";
import type {
	CompletedLesson,
	ContinueLearningLesson,
	Lesson,
	SuggestedLesson,
} from "@memorang/ui/components/LessonsHomeScreen/types";

export const allModules: Lesson[] = [
	{
		id: "sepsis-recognition-first-hour",
		title: "Sepsis Recognition and First-Hour Response",
		description:
			"An updated review of early warning signs, lactate trends, and rapid response priorities.",
		meta: { specialty: "Emergency Medicine", durationMinutes: 22 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "hypertension-management-high-risk",
		title: "Hypertension Management in High-Risk Adult Populations",
		description:
			"Compare guideline-directed therapy adjustments across common comorbidities.",
		meta: { specialty: "Primary Care", durationMinutes: 19 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "migraine-red-flags-imaging",
		title: "Migraine Red Flags That Require Immediate Imaging",
		description:
			"Learn the symptom patterns and exam findings that change workup urgency.",
		meta: { specialty: "Neurology", durationMinutes: 12 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=400&q=80",
	},
];

export const exploreModules: Lesson[] = [
	...allModules,
	{
		id: "chronic-heart-failure-staging",
		title: "Chronic Heart Failure: Staging and Long-Term Management",
		description:
			"An overview of heart failure classification by ejection fraction and the four pillars of guideline-directed medical therapy.",
		meta: { specialty: "Cardiology", durationMinutes: 25 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "type-2-diabetes-pharmacologic-management",
		title: "Type 2 Diabetes: From Metformin to Advanced Therapy",
		description:
			"A comprehensive overview of diagnostic criteria, individualized glycemic targets, and pharmacologic management.",
		meta: { specialty: "Endocrinology", durationMinutes: 25 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=400&q=80",
	},
];

export type ExploreLessonDetails = {
	isPremium: boolean;
	isCompleted: boolean;
	/** Days since the lesson content was last updated; <= 10 counts as recently updated. */
	updatedDaysAgo: number;
	popularityScore: number;
	difficultyLevel: number;
};

export const RECENT_WINDOW_DAYS = 10;

export const exploreLessonDetails: Record<string, ExploreLessonDetails> = {
	"sepsis-recognition-first-hour": {
		isPremium: false,
		isCompleted: true,
		updatedDaysAgo: 3,
		popularityScore: 92,
		difficultyLevel: 3,
	},
	"hypertension-management-high-risk": {
		isPremium: true,
		isCompleted: false,
		updatedDaysAgo: 45,
		popularityScore: 85,
		difficultyLevel: 2,
	},
	"migraine-red-flags-imaging": {
		isPremium: false,
		isCompleted: false,
		updatedDaysAgo: 8,
		popularityScore: 78,
		difficultyLevel: 2,
	},
	"chronic-heart-failure-staging": {
		isPremium: true,
		isCompleted: false,
		updatedDaysAgo: 60,
		popularityScore: 96,
		difficultyLevel: 4,
	},
	"type-2-diabetes-pharmacologic-management": {
		isPremium: false,
		isCompleted: true,
		updatedDaysAgo: 20,
		popularityScore: 88,
		difficultyLevel: 3,
	},
};

export const exploreTopicFilters: TopicFilter[] = [
	{
		title: "Cardiology",
		icon: "heart-pulse",
		description: "Heart and cardiovascular system",
		isSelected: false,
	},
	{
		title: "Neurology",
		icon: "brain",
		description: "Brain and nervous system",
		isSelected: false,
	},
	{
		title: "Emergency Medicine",
		icon: "ambulance",
		description: "Acute care and stabilization",
		isSelected: false,
	},
	{
		title: "Endocrinology",
		icon: "water",
		description: "Hormones and metabolic health",
		isSelected: false,
	},
	{
		title: "Primary Care",
		icon: "stethoscope",
		description: "Everyday outpatient medicine",
		isSelected: false,
	},
];

export const continueLearningModules: ContinueLearningLesson[] = [
	{
		id: "sepsis-recognition-first-hour",
		title: "Sepsis Recognition and First-Hour Response",
		meta: { specialty: "Emergency Medicine", durationMinutes: 22 },
		progressPercent: 68,
		timeRemainingMinutes: 8,
		thumbnailUrl:
			"https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "migraine-red-flags-imaging",
		title: "Migraine Red Flags That Require Immediate Imaging",
		meta: { specialty: "Neurology", durationMinutes: 12 },
		progressPercent: 42,
		timeRemainingMinutes: 7,
		thumbnailUrl:
			"https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=400&q=80",
	},
];

export const suggestedModules: SuggestedLesson[] = [
	{
		id: "heart-failure-triage-outpatient",
		title: "Heart Failure Triage for Outpatient Care Teams",
		description:
			"Review evidence-based escalation pathways and follow-up intervals.",
		meta: { specialty: "Cardiology", durationMinutes: 14 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "delirium-vs-dementia",
		title: "Delirium vs. Dementia: Bedside Differentiation",
		description:
			"Sharpen differential diagnosis with practical neurological cues.",
		meta: { specialty: "Neurology", durationMinutes: 16 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=400&q=80",
	},
];

export const completedModules: CompletedLesson[] = [
	{
		id: "aki-prevention-admissions",
		title: "Acute Kidney Injury Prevention in High-Risk Admissions",
		description:
			"Completed review of nephrotoxin stewardship and inpatient monitoring.",
		meta: { specialty: "Primary Care", durationMinutes: 17 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "copd-exacerbation-disposition",
		title: "COPD Exacerbation Disposition and Follow-Up Planning",
		description:
			"Finished case-based lesson on outpatient stability and return precautions.",
		meta: { specialty: "Emergency Medicine", durationMinutes: 15 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=400&q=80",
	},
];

export const moduleDetailArtifacts: ModuleArtifact[] = [
	{
		id: "overview",
		title: "Overview",
		description:
			"Key updates in cardiovascular risk assessment and preventive treatment planning.",
		statusLabel: "All read",
		status: "completed",
		iconName: "file-document-outline",
	},
	{
		id: "imaging-the-knee",
		title: "Imaging the Knee",
		description:
			"Case-based imaging review focused on patient presentation and follow-up decisions.",
		statusLabel: "32 minutes consumed",
		status: "completed",
		iconName: "play-circle-outline",
	},
	{
		id: "study-guide",
		title: "Comprehensive Study Guide",
		description:
			"Annotated visual summary covering diagnosis criteria, guideline shifts, and next steps.",
		statusLabel: "All pages viewed",
		status: "completed",
		iconName: "book-open-page-variant-outline",
	},
	{
		id: "module-assessment-quiz",
		title: "Module Assessment Quiz",
		description:
			"Evidence-based knowledge check to confirm retention before certificate release.",
		statusLabel: "20 Qs • Evaluation",
		status: "next",
		iconName: "clipboard-text-outline",
	},
];

export const moduleDetailOverallProgress: OverallProgress = {
	percent: 50,
	completedCount: 2,
	totalCount: 4,
};

export const moduleDetailMiniStats: MiniStat[] = [
	{
		iconName: "clipboard-text-outline",
		label: "Quiz accuracy",
		value: undefined,
		statusText: "Quiz locked",
	},
	{
		iconName: "clock-outline",
		label: "Study time",
		value: "45 min",
		statusText: "Studied",
	},
];

export const moduleDetailMilestones: Milestone[] = [
	{
		id: "overview",
		title: "Overview",
		description: "Completed • 45 mins",
		status: "completed",
	},
	{
		id: "podcast",
		title: "Podcast: Diagnostic Imaging",
		description: "32 min completed",
		status: "completed",
	},
	{
		id: "study-guide",
		title: "Study Guide: EKG Basics",
		description: "In progress • 12/20 pages",
		status: "in-progress",
	},
	{
		id: "quiz",
		title: "Comprehensive Quiz",
		description: "locked until study guide is complete",
		status: "locked",
	},
];
